import { api } from '@/lib/api';
import type {
  Organisation,
  PreviewData,
  ValidateRequest,
  ValidateResponse,
  ExecuteRequest,
  ExecuteResponse,
  ParseWarning,
  EntityCounts,
  ValidationError,
} from './types';
import { generateKey } from './utils';

/**
 * Fetch list of organisations available for onboarding
 */
export async function getOrganisations(): Promise<Organisation[]> {
  return api.get<Organisation[]>('/v4/legacy/v2/onboarding/organisations');
}

/**
 * Download Excel template file
 */
export async function downloadTemplate(): Promise<void> {
  const response = await fetch('/api/v4/legacy/v2/onboarding/template', {
    credentials: 'include',
  });

  // Handle session expiry - redirect to login
  if (response.status === 401) {
    const currentPath = window.location.pathname + window.location.search;
    window.location.href = `/login?url=${encodeURIComponent(currentPath)}`;
    throw new Error('Session expired');
  }

  if (!response.ok) {
    throw new Error('Failed to download template');
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'onboarding-template.xlsx';
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

/**
 * Parse uploaded Excel file for preview
 * Uses api.upload() for consistent 401 handling and error messages
 */
export async function parseExcelFile(file: File): Promise<PreviewData> {
  const formData = new FormData();
  formData.append('file', file);

  interface ParseResult {
    status: boolean;
    msg?: string;
    data?: BackendParseResponse;
  }

  const result = await api.upload<ParseResult>('/v4/legacy/v2/onboarding/parse-excel', formData);

  // Backend returns { status: boolean, data: { users, equipments, info, warnings } }
  if (!result.status) {
    throw new Error(result.msg || 'Failed to parse file');
  }

  if (!result.data) {
    throw new Error('No data returned from parser');
  }

  // Transform backend response to PreviewData format
  return transformParseResponse(result.data);
}

// Backend parse-excel response types (uses camelCase)
interface BackendParseUser {
  _row?: number;
  email?: string;
  firstName?: string;      // camelCase from backend
  lastName?: string;
  department?: string;
  position?: string;
  role?: string;
  officePhone?: string;
  extension?: string;
  cellPhone?: string;
  isAlertContact?: boolean;
}

interface BackendParseEquipment {
  _row?: number;
  inventoryNum?: string;
  building?: string;
  room?: string;
  equipmentName?: string;  // camelCase from backend
  application?: string;
  sensorType?: string;
  unit?: string;
  alertTemplate?: string;
  alertMin?: number;       // camelCase from backend
  alertMax?: number;
  department?: string;
  responsible?: string;
  manufacturer?: string;
  model?: string;
  serialNumber?: string;
  sku?: string;
  calibrationCertificate?: string;
  installationDate?: string;
  installationCertificate?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  comments?: string;
}

interface BackendParseResponse {
  info?: {
    orgName?: string;          // camelCase from backend
    quoteNum?: string;
    shippingAddress?: {
      street?: string;
      city?: string;
      country?: string;
      email?: string;
    };
    billingAddress?: {
      street?: string;
      city?: string;
      country?: string;
      email?: string;
    };
  };
  users?: BackendParseUser[];
  equipments?: BackendParseEquipment[];
  callLists?: unknown[];
  summary?: {
    totalUsers?: number;
    totalEquipments?: number;
    alertContacts?: number;
    sites?: string[];
    departments?: string[];
  };
  warnings?: Array<{
    section?: string;
    row?: number | null;
    field?: string | null;
    messageKey?: string;
    message?: string;
  }>;
}

/**
 * Transform backend parse response to frontend PreviewData format
 * Backend returns camelCase: { users, equipments, info, warnings }
 * Exported for tests.
 */
export function transformParseResponse(data: BackendParseResponse): PreviewData {
  // Transform warnings
  const warnings: ParseWarning[] = (data.warnings || []).map((w) => ({
    entity: w.section || 'general',
    row: w.row,
    column: w.field,
    messageKey: w.messageKey,
    message: w.message || w.messageKey || 'Unknown warning',
  }));

  return {
    info: {
      organisationName: data.info?.orgName || '',
      quoteNumber: data.info?.quoteNum || '',
      shippingAddress: {
        street: data.info?.shippingAddress?.street || '',
        city: data.info?.shippingAddress?.city || '',
        country: data.info?.shippingAddress?.country || '',
      },
    },
    users: (data.users || []).map((u) => ({
      _key: generateKey(),
      email: u.email || '',
      firstName: u.firstName || '',        // Already camelCase from backend
      lastName: u.lastName || '',
      role: (u.role as 'full admin' | 'group admin' | 'user' | 'read only') || 'user',
      department: u.department || '',
      cellPhone: u.cellPhone || '',
      isAlertContact: u.isAlertContact || false,
    })),
    // Backend uses "equipments" (plural) and "equipmentName"
    equipment: (data.equipments || []).map((e) => ({
      _key: generateKey(),
      building: e.building || '',
      room: e.room || '',
      name: e.equipmentName || '',         // Backend uses equipmentName
      application: mapApplicationType(e.application),
      thresholdMin: e.alertMin ?? null,    // Backend uses alertMin/alertMax
      thresholdMax: e.alertMax ?? null,
      sku: e.sku || '',
      calibrationCertificate: e.calibrationCertificate || '',
      installationDate: e.installationDate || '',
      installationCertificate: e.installationCertificate || '',
      invoiceNumber: e.invoiceNumber || '',
      invoiceDate: e.invoiceDate || '',
      comments: e.comments || '',
    })),
    warnings: warnings.length > 0 ? warnings : undefined,
  };
}

/**
 * Map a backend onboarding validation error to the frontend shape.
 * Keeps the backend `message` so the UI can show the operator the fix,
 * not just the error code. Exported for tests.
 */
export function mapValidationError(e: {
  entity: string;
  row: number | null;
  column: string;
  error: string;
  message?: string;
  value?: unknown;
}): ValidationError {
  return {
    entity: e.entity,
    row: e.row,
    column: e.column,
    error: e.error,
    value: String(e.value ?? ''),
    message: e.message,
  };
}

/**
 * Map backend application string to ApplicationType
 */
export function mapApplicationType(
  application?: string
): 'cold_storage' | 'refrigerator' | 'freezer' | 'ultra_low_freezer' | 'room_temp' | 'incubator' | 'custom' {
  if (!application) return 'custom';

  // Strip separators AND the degree symbol so the bare temperature codes the
  // backend uses ("-80C", "+4°C", "-20") normalize to "80c" / "4c" / "20". The
  // backend template parser recognizes these numeric codes; the UI must match, or
  // a customer sheet listing "-20C"/"4C"/"37C" imports every row as Custom.
  const normalized = application.toLowerCase().replace(/[\s\-_°]/g, '');

  if (normalized.includes('coldstorage') || normalized.includes('ultracold')) {
    return 'cold_storage';
  }
  if (normalized.includes('refrigerator') || normalized.includes('fridge') || normalized.includes('frigo') || normalized.includes('4c')) {
    return 'refrigerator';
  }
  if (normalized.includes('incubator') || normalized.includes('37')) {
    return 'incubator';
  }
  // -80 C ultra-low freezer must win before the generic freezer check (e.g.
  // "Freezer -80" contains both "freezer" and "80").
  if (normalized.includes('80') || normalized.includes('ultralow')) {
    return 'ultra_low_freezer';
  }
  // Room/ambient before the loose "20" freezer code so a room value never trips it.
  if (normalized.includes('room') || normalized.includes('ambient') || normalized.includes('ambiant')) {
    return 'room_temp';
  }
  if (normalized.includes('freezer') || normalized.includes('20')) {
    return 'freezer';
  }

  return 'custom';
}

/**
 * Transform PreviewData to backend format for validation/execution.
 *
 * IMPORTANT: The backend convertPreviewToSystemFormat() expects the same
 * camelCase format that /parse-excel returns — NOT snake_case. Field names
 * must match exactly: equipments (plural), equipmentName, alertMin/alertMax,
 * orgName, firstName, cellPhone, isAlertContact, etc.
 */
function transformPreviewToBackend(data: PreviewData): {
  info: {
    orgName: string;
    quoteNum: string;
    shippingAddress: {
      street: string;
      city: string;
      country: string;
    };
  };
  users: Array<{
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    department: string;
    cellPhone: string;
    isAlertContact: boolean;
  }>;
  equipments: Array<{
    building: string;
    room: string;
    equipmentName: string;
    application: string;
    alertMin: number | null;
    alertMax: number | null;
    sku: string;
    calibrationCertificate: string;
    installationDate: string;
    installationCertificate: string;
    invoiceNumber: string;
    invoiceDate: string;
    comments: string;
  }>;
} {
  return {
    info: {
      orgName: data.info.organisationName,
      quoteNum: data.info.quoteNumber,
      shippingAddress: {
        street: data.info.shippingAddress.street,
        city: data.info.shippingAddress.city,
        country: data.info.shippingAddress.country,
      },
    },
    users: data.users.map((u) => ({
      email: u.email,
      firstName: u.firstName,
      lastName: u.lastName,
      role: u.role,
      department: u.department,
      cellPhone: u.cellPhone,
      isAlertContact: u.isAlertContact,
    })),
    equipments: data.equipment.map((e) => ({
      building: e.building,
      room: e.room,
      equipmentName: e.name,
      application: e.application,
      alertMin: e.thresholdMin,
      alertMax: e.thresholdMax,
      sku: e.sku,
      calibrationCertificate: e.calibrationCertificate,
      installationDate: e.installationDate,
      installationCertificate: e.installationCertificate,
      invoiceNumber: e.invoiceNumber,
      invoiceDate: e.invoiceDate,
      comments: e.comments,
    })),
  };
}

// Backend validate response type
interface BackendValidateResponse {
  status: boolean;
  counts?: EntityCounts;
  errors?: Array<{
    entity: string;
    row: number | null;
    column: string;
    error: string;
    message?: string;
    value?: unknown;
  }>;
  parsed?: ValidateResponse['parsed'];
  existingEntities?: Record<string, number>;
}

// Onboarding /validate and /execute do heavy backend work (loadExistingEntities +
// the create pipeline) that can exceed the api client's 30s default for a large
// org — raise the per-call timeout so a slow-but-working run isn't surfaced to the
// user as a misleading "Request timed out" validation error (QUAL-6033 item 3).
const VALIDATE_TIMEOUT_MS = 120_000;
const EXECUTE_TIMEOUT_MS = 180_000;

/**
 * Validate onboarding data
 */
export async function validateOnboarding(
  request: ValidateRequest
): Promise<ValidateResponse> {
  const body: Record<string, unknown> = {
    organisation: request.organisation,
  };

  if (request.parsed) {
    body.parsed = transformPreviewToBackend(request.parsed);
  }
  if (request.csv) {
    body.csv = request.csv;
  }

  const response = await api.post<BackendValidateResponse>('/v4/legacy/v2/onboarding/validate', body, {
    timeoutMs: VALIDATE_TIMEOUT_MS,
  });

  // Transform backend response to frontend format
  return {
    status: response.status,
    counts: response.counts,
    errors: response.errors?.map(mapValidationError),
    parsed: response.parsed,
  };
}

// Backend execute response type
interface BackendExecuteResponse {
  status: boolean;
  sessionId?: string;
  dryRun?: boolean;
  counts?: EntityCounts;
  msg?: string;
  errors?: Array<{
    entity: string;
    row: number | null;
    column: string;
    error: string;
    message?: string;
    value?: unknown;
  }>;
  error?: {
    entity?: string;
    row?: number;
    message?: string;
  };
  rollbackCompleted?: boolean;
  partiallyCreated?: Record<string, number>;
  created?: {
    sensortypes?: Array<{ _id: string; name: string }>;
    usergroups?: Array<{ _id: string; name: string }>;
    sites?: Array<{ _id: string; name: string }>;
    locations?: Array<{ _id: string; name: string }>;
    sensorgroups?: Array<{ _id: string; name: string }>;
    sensors?: Array<{ _id: string; name: string }>;
    users?: Array<{
      _id: string;
      email: string;
      firstname: string;
      lastname: string;
      tempPassword?: string;
    }>;
    notificationprotocols?: Array<{ _id: string; name: string }>;
    rulegroups?: Array<{ _id: string; name: string }>;
  };
}

/**
 * Execute onboarding (create all entities)
 */
export async function executeOnboarding(
  request: ExecuteRequest
): Promise<ExecuteResponse> {
  const body: Record<string, unknown> = {
    organisation: request.organisation,
    options: request.options,
  };

  if (request.parsed) {
    body.parsed = transformPreviewToBackend(request.parsed);
  }
  if (request.csv) {
    body.csv = request.csv;
  }

  const response = await api.post<BackendExecuteResponse>('/v4/legacy/v2/onboarding/execute', body, {
    timeoutMs: EXECUTE_TIMEOUT_MS,
  });

  // Transform backend response to frontend format
  const result: ExecuteResponse = {
    status: response.status,
    sessionId: response.sessionId,
    dryRun: response.dryRun,
    counts: response.counts,
    msg: response.msg,
  };

  // Transform errors
  if (response.errors) {
    result.errors = response.errors.map(mapValidationError);
  }

  // Transform execution error
  if (response.error) {
    result.error = {
      message: response.error.message || 'Execution failed',
      entity: response.error.entity,
      row: response.error.row,
    };
  }

  // Transform rollback status (backend uses boolean, frontend uses string)
  if (response.rollbackCompleted !== undefined) {
    result.rollbackStatus = response.rollbackCompleted ? 'completed' : 'failed';
  }

  // Transform partially created entities
  if (response.partiallyCreated) {
    result.partiallyCreated = Object.entries(response.partiallyCreated)
      .filter(([, count]) => count > 0)
      .map(([entity]) => entity);
  }

  // Extract credentials from created users
  if (response.created?.users) {
    result.credentials = response.created.users
      .filter((u) => u.tempPassword)
      .map((u) => ({
        email: u.email,
        password: u.tempPassword!,
        firstName: u.firstname,
        lastName: u.lastname,
      }));
  }

  return result;
}

/**
 * Export user credentials as CSV
 */
export function exportCredentialsAsCsv(
  credentials: Array<{
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }>
): void {
  const headers = ['Email', 'Password', 'First Name', 'Last Name'];
  const rows = credentials.map((c) => [
    c.email,
    c.password,
    c.firstName,
    c.lastName,
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map((row) =>
      row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `user-credentials-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}
